<script setup lang="ts">
import {
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    useSlots,
} from "vue";
import AnotherChevroneLeft from "../../../svg/AnotherChevroneLeft.vue";
import AnotherChevroneRight from "../../../svg/AnotherChevroneRight.vue";

interface Props {
    itemsLength?: number;
    itemsPerDeifferentSizes?: Record<string, number>;
    hideButtomNavigationForMobile?: boolean;
    navigationPosition?: "bottom" | "top";
    heading?: string;
}

const props = withDefaults(defineProps<Props>(), {
    itemsLength: 0,
    //TODO: Refactor for better reusability
    hideButtomNavigationForMobile: false,
    itemsPerDeifferentSizes: () => ({
        default: 1,
        sm: 1,
        md: 2,
        lg: 3,
        xl: 3,
        "2xl": 4,
    }),
    navigationPosition: "bottom",
});

const slots = useSlots();
const hasBottomAction = computed(() => !!slots["bottom-action"]);

const swiperContainer = ref<HTMLElement | null>(null);
const selectedDot = ref(0);
const itemsPerView = ref(3);
const itemWidth = ref(0);
let isAnimating = false;
let animatingTarget = 0;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;

const scrollStep = computed(() => Math.floor(itemsPerView.value) || 1);

const dots = computed(() => {
    return Array.from(
        { length: Math.ceil(props.itemsLength / scrollStep.value) },
        (_, index) => index,
    );
});

const updateItemsPerView = () => {
    const width = window.innerWidth;
    const sizes = props.itemsPerDeifferentSizes;

    let currentItemsPerView = sizes.default ?? 1;

    if (width >= 640 && sizes.sm) currentItemsPerView = sizes.sm;
    if (width >= 768 && sizes.md) currentItemsPerView = sizes.md;
    if (width >= 1024 && sizes.lg) currentItemsPerView = sizes.lg;
    if (width >= 1280 && sizes.xl) currentItemsPerView = sizes.xl;
    if (width >= 1536 && sizes["2xl"]) currentItemsPerView = sizes["2xl"];

    itemsPerView.value = currentItemsPerView;
};

const calculateItemWidth = () => {
    const container = swiperContainer.value;

    if (container && container.firstElementChild) {
        itemWidth.value =
            (container.firstElementChild as HTMLElement).offsetWidth + 16;
    }
};

const handleResize = () => {
    updateItemsPerView();
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(async () => {
        await nextTick();
        calculateItemWidth();
    }, 50);
};

const scrollToPosition = (dotIndex: number) => {
    selectedDot.value = dotIndex;
    animatingTarget = dotIndex;
    isAnimating = true;

    const container = swiperContainer.value;

    if (container) {
        const scrollPosition = dotIndex * itemWidth.value * scrollStep.value;
        const maxScroll = container.scrollWidth - container.clientWidth;
        const safeScrollPosition = Math.min(scrollPosition, maxScroll);

        container.scrollTo({
            left: safeScrollPosition,
            behavior: "smooth",
        });

        setTimeout(() => {
            selectedDot.value = animatingTarget;
            isAnimating = false;
        }, 500);
    }
};

const swipeLeft = () => {
    if (selectedDot.value > 0) {
        scrollToPosition(selectedDot.value - 1);
    }
};

const swipeRight = () => {
    if (selectedDot.value < dots.value.length - 1) {
        scrollToPosition(selectedDot.value + 1);
    }
};

const handleScroll = () => {
    if (isAnimating) return;

    const container = swiperContainer.value;
    if (!container) return;

    const scrollPosition = container.scrollLeft;
    if (itemWidth.value === 0 || itemsPerView.value === 0) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const groupWidth = itemWidth.value * scrollStep.value;

    let clampedIndex: number;

    if (scrollPosition <= 2) {
        clampedIndex = 0;
    } else if (maxScroll - scrollPosition <= 2) {
        clampedIndex = dots.value.length - 1;
    } else {
        clampedIndex = Math.max(
            0,
            Math.min(
                Math.round(scrollPosition / groupWidth),
                dots.value.length - 1,
            ),
        );
    }

    if (selectedDot.value !== clampedIndex) {
        selectedDot.value = clampedIndex;
    }
};

watch(itemsPerView, async () => {
    selectedDot.value = 0;
    await nextTick();
    calculateItemWidth();
    scrollToPosition(0);
});

watch(
    () => props.itemsLength,
    async () => {
        await nextTick();
        calculateItemWidth();
    },
);

const snapToNearestGroup = () => {
    if (isAnimating) return;
    const container = swiperContainer.value;
    if (!container || itemWidth.value === 0) return;

    const groupWidth = itemWidth.value * scrollStep.value;
    const nearestDot = Math.round(container.scrollLeft / groupWidth);
    const clampedDot = Math.max(0, Math.min(nearestDot, dots.value.length - 1));

    if (clampedDot !== selectedDot.value) {
        scrollToPosition(clampedDot);
    } else {
        scrollToPosition(clampedDot);
    }
};

onMounted(() => {
    updateItemsPerView();
    calculateItemWidth();
    window.addEventListener("resize", handleResize);
    swiperContainer.value?.addEventListener("touchend", snapToNearestGroup, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
    if (resizeTimer) clearTimeout(resizeTimer);
    swiperContainer.value?.removeEventListener("touchend", snapToNearestGroup);
});
</script>

<template>
    <section class="flex flex-col gap-9">
        <div
            v-if="navigationPosition === 'top'"
            class="flex items-center justify-between w-full"
        >
            <h2
                class="font-bold text-28 md:text-32 lg:text-40 leading-120 text-custom-black font-mulish"
            >
                {{ heading }}
            </h2>
            <div v-if="dots.length > 1" class="flex items-center gap-5 max-md:hidden">
                <button
                    aria-label="Previous item"
                    @click="swipeLeft"
                    class="nav-btn w-[2.125rem] h-[2.125rem] flex items-center justify-center rounded-full bg-white border border-graphite-2"
                    :class="{ 'nav-btn-hidden': selectedDot <= 0 }"
                >
                    <AnotherChevroneLeft class="text-darkGreen text-12" />
                </button>

                <div class="flex gap-2">
                    <button
                        v-for="dot in dots"
                        :key="dot"
                        :aria-label="`Go to item ${dot + 1}`"
                        @click="scrollToPosition(dot)"
                        class="size-2 bg-secondary-silver rounded-full transition-all duration-300"
                        :class="{ '!bg-darkGreen w-6': selectedDot === dot }"
                    ></button>
                </div>

                <button
                    aria-label="Next item"
                    @click="swipeRight"
                    class="nav-btn w-[2.125rem] h-[2.125rem] flex items-center justify-center rounded-full bg-white border border-graphite-2"
                    :class="{
                        'nav-btn-hidden': selectedDot >= dots.length - 1,
                    }"
                >
                    <AnotherChevroneRight class="text-darkGreen text-12" />
                </button>
            </div>
        </div>
        <div
            ref="swiperContainer"
            @scroll="handleScroll"
            class="overflow-auto scroll-smooth scrollbar-hide flex gap-4 shrink-0 carousel-track"
            :style="{ '--items-per-view': itemsPerView }"
        >
            <slot></slot>
        </div>

        <div
            v-if="
                dots.length > 1 || hasBottomAction
            "
            class="flex items-center w-full"
            :class="{
                'justify-between': hasBottomAction && dots.length > 1,
                'justify-center': !hasBottomAction || dots.length > 1,
                'justify-end': hasBottomAction && dots.length <= 1,
                'md:hidden': navigationPosition === 'top',
            }"
        >
            <div
                v-if="dots.length > 1"
                class="flex items-center gap-5"
                :class="{ 'hidden md:flex': hideButtomNavigationForMobile }"
            >
                <button
                    aria-label="Previous item"
                    @click="swipeLeft"
                    class="nav-btn w-[2.125rem] h-[2.125rem] flex items-center justify-center rounded-full bg-white border border-graphite-2"
                    :class="{ 'nav-btn-hidden': selectedDot <= 0 }"
                >
                    <AnotherChevroneLeft class="text-darkGreen text-12" />
                </button>

                <div class="flex gap-2">
                    <button
                        v-for="dot in dots"
                        :key="dot"
                        :aria-label="`Go to item ${dot + 1}`"
                        @click="scrollToPosition(dot)"
                        class="size-2 bg-secondary-silver rounded-full transition-all duration-300"
                        :class="{ '!bg-darkGreen w-6': selectedDot === dot }"
                    ></button>
                </div>

                <button
                    aria-label="Next item"
                    @click="swipeRight"
                    class="nav-btn w-[2.125rem] h-[2.125rem] flex items-center justify-center rounded-full bg-white border border-graphite-2"
                    :class="{
                        'nav-btn-hidden': selectedDot >= dots.length - 1,
                    }"
                >
                    <AnotherChevroneRight class="text-darkGreen text-12" />
                </button>
            </div>

            <template v-if="hasBottomAction">
                <slot name="bottom-action"></slot>
            </template>
        </div>
    </section>
</template>

<style scoped>
.carousel-track {
    align-items: stretch;
}

.carousel-track > :deep(*) {
    flex: 0 0
        calc(
            (100% - (var(--items-per-view) - 1) * 1rem) / var(--items-per-view)
        );
    height: auto;
}

.nav-btn {
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
    opacity: 1;
    visibility: visible;
}

.nav-btn-hidden {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
}
</style>
